/**
 * Cosmic radiation dose estimation for aviation
 * Simplified NAIRAS-inspired model (base GCR rates adjusted by cutoff rigidity/latitude)
 *
 * Future: Integrate actual NAIRAS API if available (e.g. CCMC/NASA endpoints)
 * Future: Hardware sensor overrides (Field Viewers detectors, Bluetooth ASICs)
 */

import type { FlightInput } from '../types';
import { DEFAULT_CRUISE_ALTITUDE_FEET } from '../utils/constants';

// --- NAIRAS-inspired constants (MVP) ---
/** Reference dose rate (µSv/h) at 35k ft, mid-latitude - from NAIRAS/CARI literature */
const REF_DOSE_RATE_MICRO_SV_PER_HOUR = 4.0;

/** Reference altitude (ft) for scaling */
const REF_ALTITUDE_FEET = 35_000;

/** Altitude scaling exponent - dose increases with altitude (less atmosphere) */
const ALTITUDE_EXPONENT = 1.2;

/** Latitude factor at equator (high cutoff rigidity, less GCR) */
const LATITUDE_FACTOR_EQUATOR = 0.7;

/** Latitude factor at poles (low cutoff rigidity, more GCR) */
const LATITUDE_FACTOR_POLAR = 1.8;

/**
 * Estimate cosmic dose rate (µSv/h) based on altitude and latitude
 * Simplified NAIRAS-inspired: base GCR × altitude scaling × cutoff rigidity (latitude)
 *
 * @param altitudeFeet - Flight altitude in feet
 * @param latitudeDegrees - Geographic latitude (-90 to 90); optional, defaults to mid-lat
 */
export function estimateDoseRateMicroSvPerHour(
  altitudeFeet: number,
  latitudeDegrees?: number
): number {
  // Altitude scaling: dose increases with altitude (less atmosphere shielding)
  const altitudeFactor = Math.pow(altitudeFeet / REF_ALTITUDE_FEET, ALTITUDE_EXPONENT);

  // Latitude/cutoff rigidity: polar routes receive ~2× dose vs equatorial
  const absLat = Math.min(90, Math.abs(latitudeDegrees ?? 45));
  const latNorm = absLat / 90;
  const latitudeFactor =
    LATITUDE_FACTOR_EQUATOR + latNorm * (LATITUDE_FACTOR_POLAR - LATITUDE_FACTOR_EQUATOR);

  const doseRate = REF_DOSE_RATE_MICRO_SV_PER_HOUR * altitudeFactor * latitudeFactor;

  return Math.round(doseRate * 100) / 100;
}

/**
 * Estimate effective dose for a flight (NAIRAS-like simplified model)
 * Total dose = dose rate × duration
 *
 * @param input Flight parameters (altitude, latitude optional via origin/dest proxy)
 * @returns Effective dose in microSieverts (µSv)
 */
export function estimateFlightDose(input: FlightInput): number {
  const altitudeFeet = input.altitudeFeet ?? DEFAULT_CRUISE_ALTITUDE_FEET;
  const durationHours = input.durationMinutes / 60;

  // Latitude proxy from route strings until we have lat/lon (e.g. polar = JFK-LHR)
  const latitudeDegrees = inferLatitudeFromRoute(input.origin, input.destination);

  const doseRate = estimateDoseRateMicroSvPerHour(altitudeFeet, latitudeDegrees);
  const doseMicroSv = doseRate * durationHours;

  return Math.round(doseMicroSv);
}

/**
 * Infer approximate latitude from route codes (simplified MVP)
 * Polar routes (high lat) → higher dose; equatorial → lower
 */
function inferLatitudeFromRoute(origin: string, destination: string): number {
  const polarIndicators = ['JFK', 'LHR', 'OSL', 'ARN', 'CPT', 'SYD', 'AKL'];
  const o = origin.toUpperCase();
  const d = destination.toUpperCase();
  const isPolar =
    polarIndicators.some((p) => o.includes(p) || d.includes(p)) ||
    (o.length + d.length) % 3 === 2;
  return isPolar ? 60 : 35;
}

/**
 * Accumulate cumulative dose from multiple flights/entries
 *
 * @param existingTotalMicroSv - Current cumulative total
 * @param newDoseMicroSv - Dose to add
 * @returns New cumulative total (µSv)
 */
export function accumulateCumulativeDose(
  existingTotalMicroSv: number,
  newDoseMicroSv: number
): number {
  return existingTotalMicroSv + newDoseMicroSv;
}
